import { useContext, useState } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import BryntumContext from "@/react/context/bryntumContext";
import { Eye, EyeOff, Info, Key, Trash2 } from "lucide-react";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Button } from "@/modern/components/ui/button";
import { Card } from "@/modern/components/ui/card";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { toast } from "sonner";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import {
  getApiErrorMessage,
  IntegrationAlert,
  isBoardSyncRequestError,
  OPENAI_KEY_REQUIRED_TITLE,
} from "@/integrations";
import { removeBoardSecret, saveBoardSecret } from "../utils/boardSecretPreference";

const OpenAIDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project } = useContext(BryntumContext);
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasOpenAIKey = useHasOpenAIKey();
  const { canManageSecrets } = useBoardPermissions();

  const handleSaveApiKey = async () => {
    if (!project) return;

    if (!canManageSecrets) {
      toast.error("You don't have permission to update the OpenAI API key");
      return;
    }

    const trimmedKey = apiKey.trim();
    if (!trimmedKey) {
      toast.error("Please enter an API key or use the Disconnect button to remove it");
      return;
    }

    setIsSaving(true);
    try {
      await saveBoardSecret(project, PERSISTENCE_STATE_KEYS.openAiApiKey, trimmedKey);
      setApiKey("");
      setShowPassword(false);
      toast.success(hasOpenAIKey ? "OpenAI API key updated successfully" : "OpenAI API key saved successfully");
    } catch (error) {
      if (!isBoardSyncRequestError(error)) {
        toast.error(getApiErrorMessage(error, "Failed to save OpenAI API key"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveKey = async () => {
    if (!project) return;

    if (!canManageSecrets) {
      toast.error("You don't have permission to remove the OpenAI API key");
      return;
    }

    if (!hasOpenAIKey) {
      toast.error("No API key to remove");
      return;
    }

    setIsSaving(true);
    try {
      await removeBoardSecret(project, PERSISTENCE_STATE_KEYS.openAiApiKey);
      setApiKey("");
      setShowPassword(false);
      toast.success("OpenAI API key removed successfully");
    } catch (error) {
      if (!isBoardSyncRequestError(error)) {
        toast.error(getApiErrorMessage(error, "Failed to remove OpenAI API key"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      {canManageSecrets ? (
        <Card className="py-3 px-4 mb-6 max-w-3xl">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Key className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">
                  {hasOpenAIKey ? "OpenAI API Key Configured" : "Configure OpenAI API Key"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {hasOpenAIKey
                    ? "OpenAI is connected. The saved key stays hidden. Enter a new key to replace it, or disconnect it below."
                    : "Enter your OpenAI API key to enable AI-powered features in your board."}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="openai-api-key" className="text-sm font-medium">
                  OpenAI API Key
                </Label>
                {apiKey ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSaving}
                    className="h-7 px-2 text-xs"
                  >
                    {showPassword ? (
                      <>
                        <EyeOff className="h-3 w-3 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="h-3 w-3 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
              <Input
                id="openai-api-key"
                type={showPassword ? "text" : "password"}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isSaving}
                autoComplete="one-time-code"
                data-form-type="other"
                className="font-mono text-sm"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              {hasOpenAIKey ? (
                <Button
                  variant="destructive"
                  onClick={handleRemoveKey}
                  disabled={isSaving}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </Button>
              ) : null}
              <Button onClick={handleSaveApiKey} disabled={isSaving || !apiKey.trim()} size="sm">
                {isSaving ? (hasOpenAIKey ? "Updating..." : "Saving...") : hasOpenAIKey ? "Replace Key" : "Save Key"}
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {!hasOpenAIKey && (
            <IntegrationAlert
              className="mb-6"
              variant="missing_config"
              provider="openai"
              title={OPENAI_KEY_REQUIRED_TITLE}
              message="To configure AI Prompt settings, you need to set up your OpenAI API key first. The Board Owner or a Board Admin can configure the API key in settings."
            />
          )}

          {hasOpenAIKey && (
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Key className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-foreground mb-1">OpenAI API Key Configured</h4>
                  <p className="text-sm text-foreground">Your OpenAI API key is configured and ready to use.</p>
                </div>
              </div>
            </div>
          )}

          <Card className="py-3 px-4 mb-6 max-w-3xl border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/10">
            <div className="flex gap-3">
              <Info className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-900 dark:text-gray-100">
                Only the <strong>Board Owner</strong> or a <strong>Board Admin</strong> can connect, replace, or disconnect the OpenAI API key. The saved key is never shown.
              </p>
            </div>
          </Card>
        </>
      )}

      {/* Information Section */}
      <div className="mt-6 p-4 bg-card rounded-lg border border-border max-w-3xl">
        <h4 className="text-sm font-semibold text-foreground mb-2">About OpenAI Integration</h4>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">The OpenAI API key enables AI-powered features such as:</p>
        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>AI-generated image inspection and analysis</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Intelligent context-based recommendations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Automated content generation based on your custom prompts</span>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground mt-4">
          <strong>Security Note:</strong> Your API key is stored on the server and is never shown again. The Board Owner or a Board Admin can replace or disconnect it.
        </p>
      </div>
    </div>
  );
};

export default OpenAIDetail;
